import type { ExtractionCitation } from '@ctw/shared/api/ai/use-extraction-jobs';
import { Button } from '@ctw/shared/components/button';
import { notify } from '@ctw/shared/components/toast';
import { computeExportSummary } from '@ctw/shared/content/fact-extraction/compute-export-summary';
import type { MappedFactGroup } from '@ctw/shared/content/fact-extraction/map-extraction-job';
import { useCitationSources } from '@ctw/shared/content/fact-extraction/use-citation-sources';
import { useModal } from '@ctw/shared/context/modal-context';
import { useTelemetry } from '@ctw/shared/context/telemetry/telemetry-boundary';
import { tw, twx } from '@ctw/shared/utils/tailwind';
import {
  faCheck,
  faDownload,
  faFileLines,
  faFileSpreadsheet,
  faMinus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import pluralize from 'pluralize';

export interface PatientFactExtractionExportModalBodyProps {
  patientName: string;
  groups: Array<MappedFactGroup>;
  excludedIds: Array<string>;
  citations: Array<ExtractionCitation>;
  onClose: () => void;
}

export const PatientFactExtractionExportModalBody = ({
  patientName,
  groups,
  excludedIds,
  citations,
  onClose,
}: PatientFactExtractionExportModalBodyProps) => {
  // Called here (not in the page) so counts update live if resolution finishes
  // while the modal is open. The query is cached, shared with the page's call.
  const { sourceByCitationId, isLoading } = useCitationSources(citations);
  const summary = computeExportSummary({ groups, excludedIds, sourceByCitationId });
  const totalFieldsCount = summary.includedFieldsCount + summary.excludedFieldsCount;
  // Require at least one documented field — an export of only empty fields is useless.
  const canDownload = !isLoading && summary.totalDocumentedIncludedCount > 0;

  // Groups contributing nothing to the export sink to the bottom of the list.
  const orderedGroupSummaries = [
    ...summary.groupSummaries.filter((group) => group.documentedIncludedCount > 0),
    ...summary.groupSummaries.filter((group) => group.documentedIncludedCount === 0),
  ];

  const handleDownload = () => {
    // TODO: actually Download the Thing
    onClose();
    notify({
      type: 'success',
      title: `Export ready - ${pluralize('field', summary.totalDocumentedIncludedCount, true)}, ${pluralize('source document', summary.includedSourcesCount, true)}`,
    });
  };

  return (
    <div className={tw`flex w-[480px] max-w-full flex-col`}>
      <div className={tw`border-divider-main border-b px-6 py-5`}>
        <div className={tw`font-semibold text-content-main text-lg`}>Export Package</div>
        <div className={tw`mt-0.5 text-content-subtle text-sm`}>{patientName}</div>
      </div>
      <div className={tw`space-y-5 px-6 py-5`}>
        <div className={tw`space-y-4`}>
          <div className={tw`flex items-start gap-3`}>
            <FontAwesomeIcon
              icon={faFileSpreadsheet}
              className={tw`mt-0.5 h-4 w-4 text-primary-main`}
            />
            <div>
              <div className={tw`font-semibold text-content-main text-sm`}>Field Data</div>
              <div className={tw`text-content-subtle text-xs`}>
                CSV · {summary.totalDocumentedIncludedCount} of{' '}
                {pluralize('field', totalFieldsCount, true)} included
              </div>
            </div>
          </div>
          <div className={tw`flex items-start gap-3`}>
            <FontAwesomeIcon icon={faFileLines} className={tw`mt-0.5 h-4 w-4 text-caution-main`} />
            <div>
              <div className={tw`font-semibold text-content-main text-sm`}>Sources</div>
              <div className={tw`text-content-subtle text-xs`}>
                {isLoading
                  ? 'Resolving sources...'
                  : `${summary.includedSourcesCount} of ${pluralize('source', summary.totalSourcesCount, true)} included`}
              </div>
            </div>
          </div>
        </div>
        <div className={tw`space-y-1.5`}>
          {orderedGroupSummaries.map((group) => {
            const included = group.documentedIncludedCount > 0;
            return (
              <div key={group.label} className={tw`flex items-center gap-2 text-sm`}>
                <FontAwesomeIcon
                  icon={included ? faCheck : faMinus}
                  className={twx('h-3.5 w-3.5 shrink-0', {
                    'text-success-main': included,
                    'text-content-icon': !included,
                  })}
                />
                <span
                  className={twx('min-w-0 break-words', {
                    'text-content-main': included,
                    'text-content-subtle': !included,
                  })}
                >
                  {group.label}
                </span>
                <span className={tw`whitespace-nowrap text-content-subtle`}>
                  · {pluralize('field', group.documentedIncludedCount, true)}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div
        className={tw`flex items-center justify-between gap-3 border-divider-main border-t px-6 py-4`}
      >
        <div className={tw`text-content-subtle text-sm`}>
          1 spreadsheet + {pluralize('source', summary.includedSourcesCount, true)}
        </div>
        <div className={tw`flex gap-3`}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" onClick={handleDownload} disabled={!canDownload}>
            <div className={tw`flex items-center space-x-2 whitespace-nowrap`}>
              <FontAwesomeIcon icon={faDownload} className={tw`h-3.5 w-3.5`} />
              <span>Download ZIP</span>
            </div>
          </Button>
        </div>
      </div>
    </div>
  );
};

export const usePatientFactExtractionExportModal = () => {
  const telemetry = useTelemetry();
  const { openModal, closeModal } = useModal({
    boundaryName: 'AtlasExportModal',
    layout: {
      mode: 'fit',
      overflowBehavior: 'visible',
      fullbleed: true,
      showCloseButton: true,
    },
  });

  return (props: Omit<PatientFactExtractionExportModalBodyProps, 'onClose'>) => {
    openModal({
      telemetry,
      action: {
        label: 'Export Package',
        title: props.patientName,
        content: {
          body: <PatientFactExtractionExportModalBody {...props} onClose={closeModal} />,
        },
      },
    });
  };
};
